import React, { useEffect, useMemo, useState } from 'react';

const todayYmd = () => new Date().toISOString().slice(0, 10);

const emptyMed = () => ({ name: '', dosage: '', frequency: '' });

const safeParseJson = (s, fallback) => {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
};

export default function NurseMeds({ token }) {
  const [medProfiles, setMedProfiles] = useState([]);
  const [logs, setLogs] = useState([]);

  const [, setLoadingProfiles] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState('');

  const [date, setDate] = useState(todayYmd());

  // add/update profile form
  const [id, setId] = useState('');
  const [camperFirst, setCamperFirst] = useState('');
  const [camperLast, setCamperLast] = useState('');
  const [dob, setDob] = useState('');
  const [notes, setNotes] = useState('');

  // meds editor UI (stored as medsJson in sheet)
  const [meds, setMeds] = useState([emptyMed()]);

  const fetchProfiles = async () => {
    setError('');
    setLoadingProfiles(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-camper-meds', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to load meds: ${res.status}`);
        setMedProfiles([]);
        return;
      }

      setMedProfiles(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      setError('Network error while loading med profiles');
      setMedProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const fetchLogs = async (d) => {
    setError('');
    setLoadingLogs(true);
    try {
      const res = await fetch(`/.netlify/functions/admin-get-med-logs?date=${encodeURIComponent(d)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to load logs: ${res.status}`);
        setLogs([]);
        return;
      }

      setLogs(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      setError('Network error while loading logs');
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchLogs(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, date]);

  const normalizedMedsForSave = useMemo(() => {
    const cleaned = (meds || [])
      .map((m) => ({
        name: String(m?.name || '').trim(),
        dosage: String(m?.dosage || '').trim(),
        frequency: String(m?.frequency || '').trim()
      }))
      .filter((m) => m.name || m.dosage || m.frequency);

    return cleaned;
  }, [meds]);

  const upsertProfile = async () => {
    setError('');

    if (!camperFirst.trim() || !camperLast.trim() || !dob.trim()) {
      setError('Camper first, last, and DOB are required.');
      return;
    }

    if (normalizedMedsForSave.length === 0) {
      setError('Please add at least one medication (or enter one row).');
      return;
    }

    const payload = {
      id: id || '',
      camperFirst: camperFirst.trim(),
      camperLast: camperLast.trim(),
      dob: dob.trim(),
      medsJson: JSON.stringify(normalizedMedsForSave),
      notes: notes.trim()
    };

    try {
      const res = await fetch('/.netlify/functions/admin-upsert-camper-meds', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to save: ${res.status}`);
        return;
      }

      // clear form
      setId('');
      setCamperFirst('');
      setCamperLast('');
      setDob('');
      setNotes('');
      setMeds([emptyMed()]);

      await fetchProfiles();
    } catch (e) {
      setError('Network error while saving med profile');
    }
  };

  const loadIntoForm = (row) => {
    setId(String(row.id || '').trim());
    setCamperFirst(String(row.camperFirst || '').trim());
    setCamperLast(String(row.camperLast || '').trim());
    setDob(String(row.dob || '').trim());
    setNotes(String(row.notes || '').trim());

    // Backwards compatible:
    // - old data might have {name,dosage,times:[...]} so we convert times -> frequency
    const parsed = safeParseJson(String(row.medsJson || '[]'), []);
    const arr = Array.isArray(parsed) ? parsed : [];

    const converted = arr
      .map((m) => {
        const name = String(m?.name || '').trim();
        const dosage = String(m?.dosage || '').trim();
        let frequency = String(m?.frequency || '').trim();

        if (!frequency && Array.isArray(m?.times)) {
          frequency = m.times.map((t) => String(t || '').trim()).filter(Boolean).join(', ');
        }

        return { name, dosage, frequency };
      })
      .filter((m) => m.name || m.dosage || m.frequency);

    setMeds(converted.length ? converted : [emptyMed()]);
  };

  const clearForm = () => {
    setId('');
    setCamperFirst('');
    setCamperLast('');
    setDob('');
    setNotes('');
    setMeds([emptyMed()]);
  };

  const addLog = async ({ camperMedsId, camperFirst, camperLast, dob, slot, status }) => {
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-add-med-log', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          camperMedsId,
          camperFirst,
          camperLast,
          dob,
          date,
          slot,
          status,
          notes: ''
        })
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setError(json?.error || `Failed to log: ${res.status}`);
        return;
      }

      await fetchLogs(date);
    } catch (e) {
      setError('Network error while logging med pass');
    }
  };

  const logIndex = useMemo(() => {
    const m = new Map();
    (logs || []).forEach((l) => {
      const key = `${l.camperMedsId}|${l.date}|${String(l.slot || '').toUpperCase()}`;
      m.set(key, l);
    });
    return m;
  }, [logs]);

  // Dropdown helpers (MUST be above return)
  const statusToSelectValue = (status) => {
    const s = String(status || '').toUpperCase();

    // Backwards compatible with old values
    if (s === 'GIVEN') return 'TAKEN';
    if (s === 'REFUSED') return 'REJECTED';

    if (s === 'TAKEN' || s === 'ABSENT' || s === 'NA' || s === 'REJECTED') return s;
    return '';
  };

  const handleSelectStatus = ({ p, pid, slot, nextValue }) => {
    if (!nextValue) return;

    return addLog({
      camperMedsId: pid,
      camperFirst: p.camperFirst,
      camperLast: p.camperLast,
      dob: p.dob,
      slot,
      status: nextValue
    });
  };

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      {/* -------------------------
          Profile Editor
      ------------------------- */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'baseline'
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Meds</h2>
            <div className="muted" style={{ marginTop: 4 }}>
              Maintain med profiles and log AM / Afternoon / PM med pass.
            </div>
          </div>
        </div>

        {error ? (
          <div className="error" style={{ marginTop: 10 }}>
            {error}
          </div>
        ) : null}

        {/* Camper info */}
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <input
            value={camperFirst}
            onChange={(e) => setCamperFirst(e.target.value)}
            placeholder="Camper first"
            style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
          />
          <input
            value={camperLast}
            onChange={(e) => setCamperLast(e.target.value)}
            placeholder="Camper last"
            style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
          />
          <input
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            placeholder="DOB (e.g. 7/9/2024)"
            style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
          />
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
          />
        </div>

        {/* Medication list */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Medications</div>

          <div style={{ display: 'grid', gap: 8 }}>
            {(meds || []).map((m, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 1fr auto',
                  gap: 8,
                  alignItems: 'center'
                }}
              >
                <input
                  value={m.name}
                  onChange={(e) =>
                    setMeds((prev) => prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))
                  }
                  placeholder="Medication name"
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
                />
                <input
                  value={m.dosage}
                  onChange={(e) =>
                    setMeds((prev) => prev.map((x, i) => (i === idx ? { ...x, dosage: e.target.value } : x)))
                  }
                  placeholder="Dosage (e.g. 10mg, 1 tablet)"
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
                />
                <input
                  value={m.frequency}
                  onChange={(e) =>
                    setMeds((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, frequency: e.target.value } : x))
                    )
                  }
                  placeholder="Frequency (e.g. AM, 2x/day, before lunch)"
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
                />
                <button
                  className="btn"
                  type="button"
                  onClick={() => setMeds((prev) => prev.filter((_, i) => i !== idx))}
                  disabled={(meds || []).length <= 1}
                  title={(meds || []).length <= 1 ? 'Keep at least one row' : 'Remove'}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" type="button" onClick={() => setMeds((prev) => [...prev, emptyMed()])}>
              + Add medication
            </button>

            <div className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              Saved to Sheet as <code>medsJson</code>.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={upsertProfile}>
            {id ? 'Update profile' : 'Add profile'}
          </button>

          {id ? (
            <button className="btn" type="button" onClick={clearForm}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {/* -------------------------
          Med Pass Log (dropdown)
      ------------------------- */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'baseline'
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Med pass log</h3>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="muted" style={{ fontSize: 12 }}>
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd' }}
            />
            <button className="btn" onClick={() => fetchLogs(date)} disabled={loadingLogs}>
              Refresh logs
            </button>
          </div>
        </div>

        {loadingLogs ? <div style={{ marginTop: 10 }}>Loading logs…</div> : null}

        {!medProfiles.length ? (
          <div className="muted" style={{ marginTop: 10 }}>
            No med profiles yet.
          </div>
        ) : (
          <div style={{ marginTop: 10, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Camper', 'DOB', 'AM', 'AFTERNOON', 'PM', 'Edit profile'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #eee',
                        fontSize: 12,
                        color: '#666'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {medProfiles.map((p) => {
                  const camper = `${p.camperLast || ''}, ${p.camperFirst || ''}`.trim().replace(/^, /, '') || '—';
                  const pid = String(p.id || '').trim();

                  const keyAM = `${pid}|${date}|AM`;
                  const keyA = `${pid}|${date}|AFTERNOON`;
                  const keyPM = `${pid}|${date}|PM`;

                  const logAM = logIndex.get(keyAM);
                  const logA = logIndex.get(keyA);
                  const logPM = logIndex.get(keyPM);

                  const chip = (log) => {
                    if (!log) return <span className="muted">—</span>;
                    const s = String(log.status || '').toUpperCase();
                    return <span style={{ fontWeight: 700 }}>{s}</span>;
                  };

                  const selectStyle = {
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid #ddd',
                    background: '#fff'
                  };

                  return (
                    <tr key={pid || camper}>
                      <td
                        style={{
                          padding: '8px 10px',
                          borderBottom: '1px solid #f1f1f1',
                          fontWeight: 700
                        }}
                      >
                        {camper}
                      </td>

                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                        {p.dob || '—'}
                      </td>

                      {/* AM */}
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                        {chip(logAM)}
                        <div style={{ marginTop: 6 }}>
                          <select
                            value={statusToSelectValue(logAM?.status)}
                            onChange={(e) =>
                              handleSelectStatus({
                                p,
                                pid,
                                slot: 'AM',
                                nextValue: e.target.value
                              })
                            }
                            style={selectStyle}
                          >
                            <option value="">—</option>
                            <option value="TAKEN">Taken</option>
                            <option value="ABSENT">Absent</option>
                            <option value="NA">N/A</option>
                            <option value="REJECTED">Rejected</option>
                          </select>
                        </div>
                      </td>

                      {/* AFTERNOON */}
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                        {chip(logA)}
                        <div style={{ marginTop: 6 }}>
                          <select
                            value={statusToSelectValue(logA?.status)}
                            onChange={(e) =>
                              handleSelectStatus({
                                p,
                                pid,
                                slot: 'AFTERNOON',
                                nextValue: e.target.value
                              })
                            }
                            style={selectStyle}
                          >
                            <option value="">—</option>
                            <option value="TAKEN">Taken</option>
                            <option value="ABSENT">Absent</option>
                            <option value="NA">N/A</option>
                            <option value="REJECTED">Rejected</option>
                          </select>
                        </div>
                      </td>

                      {/* PM */}
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                        {chip(logPM)}
                        <div style={{ marginTop: 6 }}>
                          <select
                            value={statusToSelectValue(logPM?.status)}
                            onChange={(e) =>
                              handleSelectStatus({
                                p,
                                pid,
                                slot: 'PM',
                                nextValue: e.target.value
                              })
                            }
                            style={selectStyle}
                          >
                            <option value="">—</option>
                            <option value="TAKEN">Taken</option>
                            <option value="ABSENT">Absent</option>
                            <option value="NA">N/A</option>
                            <option value="REJECTED">Rejected</option>
                          </select>
                        </div>
                      </td>

                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                        <button className="btn" onClick={() => loadIntoForm(p)}>
                          Edit/View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
              Logging a slot twice updates the existing entry (no duplicates).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}